import React, {useCallback, useLayoutEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {Detail, EMPHASIS, SplitButton} from '@forecast-it/design-system';
// eslint-disable-next-line import/no-internal-modules -- BORDER is not re-exported from the barrel
import {BORDER} from '@forecast-it/design-system/dist/forecast-v2/components/controls/buttons/BaseButton';
import {useIntl} from 'react-intl';
import PreviewErrorState from './PreviewErrorState';
import {resolveTemplate} from './resolver/resolveTemplate';
import type {MergeContext, QuoteTemplate} from './resolver/types';
import QuotePreview from './preview/QuotePreview';

type Props = {
	template: QuoteTemplate;
	mergeContext: MergeContext;
};

const ZOOM_MIN = 0.3;
const ZOOM_MAX = 1.2;
const ZOOM_STEP = 0.1;
const ZOOM_RESET = 1;

/** Keep the level on a clean 10% stop — repeated float steps otherwise drift to 89.99999%. */
const clampZoom = (value: number): number => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(value * 100) / 100));

const Pane = styled.div`
	flex: 1;
	min-width: 0;
	min-height: 0;
	display: flex;
	flex-direction: column;
`;

const Toolbar = styled.div`
	flex: none;
	height: 52px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 0 16px;
	box-sizing: border-box;
	background: ${({theme}) => theme.sys.color.background.neutral['1']};
	border-bottom: 1px solid ${({theme}) => theme.sys.color.border.decorative};
`;

const ToolbarRight = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;

const Scroller = styled.div`
	flex: 1;
	min-width: 0;
	min-height: 0;
	overflow-y: auto;
	padding: 24px 16px;
	box-sizing: border-box;
`;

/*
 * Zoom, not transform: a scaled transform leaves the scroller's scroll height at the unscaled
 * size, so at 120% the last sheet cannot be reached.
 */
const Stack = styled.div<{$zoom: number}>`
	zoom: ${p => p.$zoom};
`;

class PreviewErrorBoundary extends React.Component<
	{fallback: React.ReactNode; resetKey: number; children: React.ReactNode},
	{errored: boolean}
> {
	static getDerivedStateFromError() {
		return {errored: true};
	}
	state = {errored: false};
	componentDidUpdate(prev: {resetKey: number}) {
		if (prev.resetKey !== this.props.resetKey && this.state.errored) {
			this.setState({errored: false});
		}
	}
	render() {
		if (this.state.errored) return this.props.fallback;
		return this.props.children;
	}
}

/**
 * The live preview renders the document as HTML (the same pages and computed content the PDF
 * export produces), so every settings change repaints in place: no PDF regeneration while
 * editing, no viewer reload, and the scroll position survives edits. The actual PDF is only
 * built when the user downloads it or creates the quote.
 *
 * The toolbar carries the zoom and a page indicator; both are view state and neither is stored on
 * the quote.
 */
const PreviewPane: React.FC<Props> = ({template, mergeContext}) => {
	const intl = useIntl();
	const [retryKey, setRetryKey] = useState(0);
	const [zoom, setZoom] = useState(ZOOM_RESET);
	const [visiblePage, setVisiblePage] = useState(1);
	const [pageCount, setPageCount] = useState(1);
	const scrollerRef = useRef<HTMLDivElement>(null);
	// Resolving walks every mention in every section and the preview then rebuilds five sheets,
	// including the whole estimate. Unmemoized that ran again on each scroll event and each zoom
	// click, because both set state on this component without the document having changed.
	const resolved = useMemo(() => resolveTemplate(template, mergeContext), [template, mergeContext]);

	// The indicator names the sheet at the TOP of the viewport, so an unscrolled preview reads
	// "Page 1" rather than jumping to whichever sheet happens to cross the midpoint.
	const trackPage = useCallback(() => {
		const scroller = scrollerRef.current;
		if (!scroller) return;
		const sheets = scroller.querySelectorAll<HTMLElement>('[data-cy="quote-preview-page"]');
		if (!sheets.length) return;
		// Both sides are read off the same viewport-relative box. offsetTop is measured from the
		// nearest POSITIONED ancestor, which is somewhere up in the app shell - comparing it with
		// the scroller's own scrollTop put the app header's height between the two, so the
		// indicator only turned over after that much extra scrolling. Rects also survive the
		// zoom applied to the stack, which scales offsetTop but not scrollTop.
		const threshold = scroller.getBoundingClientRect().top + 24;
		let current = 1;
		sheets.forEach((sheet, index) => {
			if (sheet.getBoundingClientRect().top <= threshold) current = index + 1;
		});
		setPageCount(sheets.length);
		setVisiblePage(current);
	}, []);

	// Each page is exactly one sheet, so the count moves only when a section is toggled. Keying on
	// that keeps the indicator right after a toggle without re-measuring on every zoom click.
	const sectionSignature = template.sections.map(section => `${section.type}:${section.enabled}`).join('|');
	useLayoutEffect(trackPage, [trackPage, sectionSignature]);

	const percent = Math.round(zoom * 100);

	return (
		<Pane>
			<Toolbar data-cy="quote-preview-toolbar">
				<Detail>{intl.formatMessage({id: 'quote_designer.preview.live'})}</Detail>
				<ToolbarRight>
					<Detail>
						{intl.formatMessage({id: 'quote_designer.preview.page_of'}, {page: visiblePage, total: pageCount})}
					</Detail>
					<SplitButton gap="none">
						<SplitButton.Section
							icon="zoomOut"
							emphasis={EMPHASIS.MONO}
							disabled={zoom <= ZOOM_MIN}
							aria-label={intl.formatMessage({id: 'quote_designer.preview.zoom_out'})}
							onPress={() => setZoom(current => clampZoom(current - ZOOM_STEP))}
						/>
						<SplitButton.Section
							text={`${percent}%`}
							emphasis={EMPHASIS.MONO}
							borderLeft={BORDER.NONE}
							borderRight={BORDER.NONE}
							aria-label={intl.formatMessage({id: 'quote_designer.preview.zoom_reset'})}
							onPress={() => setZoom(ZOOM_RESET)}
						/>
						<SplitButton.Section
							icon="zoomIn"
							emphasis={EMPHASIS.MONO}
							disabled={zoom >= ZOOM_MAX}
							aria-label={intl.formatMessage({id: 'quote_designer.preview.zoom_in'})}
							onPress={() => setZoom(current => clampZoom(current + ZOOM_STEP))}
						/>
					</SplitButton>
				</ToolbarRight>
			</Toolbar>
			<Scroller ref={scrollerRef} onScroll={trackPage} data-cy="quote-preview-scroller">
				<Stack $zoom={zoom}>
					<PreviewErrorBoundary
						resetKey={retryKey}
						fallback={<PreviewErrorState onRetry={() => setRetryKey(k => k + 1)} />}
					>
						<QuotePreview data={mergeContext.data} template={resolved} />
					</PreviewErrorBoundary>
				</Stack>
			</Scroller>
		</Pane>
	);
};

export default PreviewPane;
